/**
 * Provides an administrative interface for managing dashboard widget
 * configuration and layout preferences by role. Enables administrators
 * to customize widget display order through drag-and-drop interactions,
 * configure widget layout settings, and control how dashboard content
 * is organized for different user roles. Supports role-specific dashboard
 * personalization, change tracking, save operations, and restoration of
 * previously saved configurations.
 */
import React, { useEffect, useState } from "react";

import { getDashboardConfig, updateDashboardConfig } from "@/services/widgets";
import { ROLE_NAME_MAP, WIDGET_NAME_MAP } from "@/shared/utils/maps";
import { Edit02 } from "@untitledui/icons";

const DashboardConfig: React.FC = () => {
  const [data, setData] = useState<Record<string, any[]>>({});
  const [roles, setRoles] = useState<string[]>([]);
  const [role, setRole] = useState("");

  const [widgets, setWidgets] = useState<any[]>([]);
  const [initial, setInitial] = useState("");
  const [editing, setEditing] = useState<string | null>(null);

  const [dragIndex, setDragIndex] = useState<number | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (role) {
      const list = data[role] || [];
      setWidgets(list);
      setInitial(JSON.stringify(list));
    } else {
      setWidgets([]);
      setInitial("");
    }
  }, [role, data]);

  const loadData = async () => {
    const res = await getDashboardConfig();
    setRoles(res.roles || []);
    setData(res.data || {});
  };

  const handleDragStart = (index: number) => {
    setDragIndex(index);
  };

  const handleDrop = (index: number) => {
    if (dragIndex === null) return;

    const updated = [...widgets];
    const dragged = updated.splice(dragIndex, 1)[0];
    updated.splice(index, 0, dragged);

    const reordered = updated.map((w, i) => ({
      ...w,
      display_order: i + 1,
    }));

    setWidgets(reordered);
    setDragIndex(null);
  };

  const hasChanges = initial !== "" && JSON.stringify(widgets) !== initial;

  const handleSave = async () => {
    await updateDashboardConfig({
      roleCode: role,
      widgets,
      updatedBy: "Admin",
    });

    setInitial(JSON.stringify(widgets));
  };

  const handleCancel = () => {
    setWidgets(JSON.parse(initial));
  };

  return (
    <div className="border border-gray-200 rounded-md bg-[#fbfaf7] p-3 space-y-3">
      <div className="space-y-1 mb-2">
        <div className="text-xs text-gray-800 font-medium">Modify widget display order</div>
        <div className="text-[11px] text-gray-400">Drag and drop widgets to change their display order, and use the edit icon to customize their layout.</div>
      </div>

      <select
        className="w-full border border-gray-300 bg-white px-3 py-1.5 rounded-md text-sm text-gray-800
                   focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
        value={role}
        onChange={(e) => setRole(e.target.value)}
      >
        <option value="">Select Role</option>
        {roles.map((r, index) => (
          <option key={`${r}-${index}`} value={r}>
            {ROLE_NAME_MAP[r] || r}
          </option>
        ))}
      </select>

      {role && (
        <div className="space-y-1">
          {widgets.map((w, index) => (
            <div
  key={`${w.feature_code}-${index}`}
  draggable
  onDragStart={() => handleDragStart(index)}
  onDragOver={(e) => e.preventDefault()}
  onDrop={() => handleDrop(index)}
  className="px-3 py-2 border border-gray-200 rounded-md bg-white cursor-move flex items-center justify-between hover:bg-[#f7f5f1]"
>
  <span className="text-xs text-gray-800 truncate">
    {WIDGET_NAME_MAP[w.feature_code] || w.feature_code}
  </span>

  <div className="flex items-center gap-3">
    {editing === w.feature_code ? (
      <select
        value={w.layout}
        onChange={(e) => {
          const updated = widgets.map((item) =>
            item.feature_code === w.feature_code
              ? { ...item, layout: e.target.value }
              : item
          );

          setWidgets(updated);
        }}
        className="border rounded px-2 py-1 text-xs"
      >
        <option value="1">1</option>
        <option value="2">2</option>
        <option value="3">3</option>
      </select>
    ) : (
      <span className="text-xs text-gray-500">
        Layout: {w.layout}
      </span>
    )}

    <button
      type="button"
      onClick={() =>
        setEditing(
          editing === w.feature_code ? null : w.feature_code
        )
      }
      className="text-gray-500 hover:text-orange-600"
    >
      <Edit02 size={14} />
    </button>

    <span className="text-xs text-gray-400">⋮⋮</span>
  </div>
</div>
          ))}
        </div>
      )}

      <div className="flex justify-end items-center gap-2 pt-1">
        {hasChanges && (
          <div className="text-orange-600 border border-orange-300 bg-orange-50 px-2 py-0.5 rounded text-xs">
            Unsaved
          </div>
        )}

        <button
          className="bg-[#e7e5e0] px-3 py-1 rounded text-xs"
          onClick={handleCancel}
          disabled={!hasChanges}
        >
          Cancel
        </button>

        <button
          className="bg-[var(--color-brand)]  text-white px-3 py-1 rounded text-xs disabled:opacity-50"
          onClick={handleSave}
          disabled={!hasChanges}
        >
          Save
        </button>
      </div>
    </div>
  );
};

export default DashboardConfig;
